import { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'

import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import TableRowsIcon from '@mui/icons-material/TableRows'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import LinkIcon from '@mui/icons-material/Link'
import StorageIcon from '@mui/icons-material/Storage'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import PageContainer from '@/components/ui/containers/PageContainer'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import AccessDenied from '@/components/ui/template/AccessDenied'
import { ExternalService } from '@/constants/external-services'
import { AirtableBaseListItem, AirtableSetupVerificationResult, ExportMedConnectPatientsToAirtableResult } from '@/core/models/establishment.model'
import {
  useDiscoverAirtableBasesMutation,
  useEnableAirtableInboundSyncMutation,
  useExportMedConnectPatientsToAirtableMutation,
  useGetAirtableConfigQuery,
  useGetAirtableInboundSyncStatusQuery,
  useGetAirtablePatientsTableStatusQuery,
  useInitializeAirtablePatientsTableMutation,
  useUpdateAirtableConfigMutation,
  useVerifyAirtableInboundSyncMutation,
} from '@/core/services/admin'
import { useGetLicensesQuery } from '@/core/services/license'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { getEnv } from '@/utils/helpers'

const formSchema = z.object({
  baseId: z.string().optional(),
  apiKey: z.string().optional(),
})

type AirtableFormValues = z.infer<typeof formSchema>

function optionLabel(name: string, id: string): string {
  return `${name} (${id})`
}

type SectionCardProps = {
  stepNumber: number
  icon: React.ReactNode
  title: string
  description?: string
  done?: boolean
  disabled?: boolean
  children: React.ReactNode
}

function SectionCard({ stepNumber, icon, title, description, done, disabled, children }: SectionCardProps) {
  const { t } = useTranslation()

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: disabled ? 0.65 : 1,
        borderColor: done ? 'success.light' : undefined,
        transition: 'border-color 0.2s, opacity 0.2s',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: done ? 'success.main' : 'primary.main',
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {done ? <CheckCircleIcon fontSize="small" /> : icon || stepNumber}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
              {t('page.admin.airtable.card.step', { defaultValue: 'Step {{stepNumber}}', stepNumber })}
            </Typography>
            {done && (
              <Chip
                size="small"
                color="success"
                label={t('page.admin.airtable.card.done', { defaultValue: 'Done' })}
                sx={{ height: 18, fontSize: 11 }}
              />
            )}
          </Stack>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2} sx={{ flex: 1 }}>
        {children}
      </Stack>
    </Paper>
  )
}

type StatCardProps = {
  label: string
  value: number
  color?: 'primary' | 'success' | 'warning' | 'error'
}

function StatCard({ label, value, color = 'primary' }: StatCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.default',
        textAlign: 'center',
      }}
    >
      <Typography variant="h4" color={`${color}.main`} sx={{ fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Paper>
  )
}

const ACTION_BUTTON_SX = { minWidth: 200, height: 40 } as const

export default function AdminAirtablePage() {
  const { t } = useTranslation()
  const [bases, setBases] = useState<AirtableBaseListItem[]>([])
  const [exportResult, setExportResult] = useState<ExportMedConnectPatientsToAirtableResult>()
  const [verificationResult, setVerificationResult] = useState<AirtableSetupVerificationResult>()
  const [copiedWebhook, setCopiedWebhook] = useState(false)
  const [copiedDiagnostic, setCopiedDiagnostic] = useState(false)
  const authUser = useAppSelector(selectAuthUser)
  const establishmentId = authUser?.establishment?._id
  const { data: licenses, isLoading: isLoadingLicenses } = useGetLicensesQuery({ id: authUser?.licenses }, { skip: !authUser?.licenses.length })
  const hasAirtableLicense = useMemo(() => _.uniq(_.flatten(licenses?.map((license) => license.services))).includes(ExternalService.airtable), [licenses])

  const { data: config, isLoading: isLoadingConfig } = useGetAirtableConfigQuery(establishmentId || '', { skip: !establishmentId || !hasAirtableLicense })
  const { data: patientsTableStatus, isLoading: isLoadingPatientsTableStatus } = useGetAirtablePatientsTableStatusQuery(establishmentId || '', {
    skip: !establishmentId || !hasAirtableLicense || !config?.baseId || !config.hasApiKey,
  })
  const patientsTableReady = !!patientsTableStatus?.exists && patientsTableStatus.missingFieldNames.length === 0
  const { data: inboundSyncStatus } = useGetAirtableInboundSyncStatusQuery(establishmentId || '', {
    skip: !establishmentId || !hasAirtableLicense || !patientsTableReady,
  })
  const [discoverBases, { isLoading: isDiscoveringBases }] = useDiscoverAirtableBasesMutation()
  const [updateConfig, { isLoading: isUpdating }] = useUpdateAirtableConfigMutation()
  const [initializePatientsTable, { isLoading: isInitializingPatientsTable }] = useInitializeAirtablePatientsTableMutation()
  const [exportMedConnectPatientsToAirtable, { isLoading: isExportingMedConnectPatients }] = useExportMedConnectPatientsToAirtableMutation()
  const [enableAirtableInboundSync, { isLoading: isEnablingInboundSync }] = useEnableAirtableInboundSyncMutation()
  const [verifyAirtableInboundSync, { isLoading: isVerifyingInboundSync }] = useVerifyAirtableInboundSyncMutation()

  const form = useForm<AirtableFormValues>({
    defaultValues: {
      baseId: '',
      apiKey: '',
    },
    resolver: zodResolver(formSchema),
  })

  useEffect(() => {
    if (!config) return
    form.reset({
      baseId: config.baseId || '',
      apiKey: '',
    })
  }, [config, form])

  const apiKey = useWatch({ control: form.control, name: 'apiKey' })
  const selectedBaseId = useWatch({ control: form.control, name: 'baseId' })
  const isLoading = isLoadingLicenses || isLoadingConfig
  const hasSavedConnection = !!config?.hasApiKey && !!config.baseId
  const inboundSyncDone = !!inboundSyncStatus?.enabled && !!inboundSyncStatus.webhookConfigured
  const activeStep = !hasSavedConnection ? 0 : !patientsTableReady ? 1 : !inboundSyncDone ? 3 : 4
  const canSetupPatientsTable = !!establishmentId && hasSavedConnection
  const canMirrorPatients = !!establishmentId && patientsTableReady
  const apiBaseUrl = (getEnv('VITE_API_URL') || window.location.origin).replace(/\/$/, '')
  const webhookUrl = inboundSyncStatus?.webhookNotificationUrl || (inboundSyncStatus?.webhookPath ? apiBaseUrl + inboundSyncStatus.webhookPath : undefined)
  const baseOptions = useMemo(
    () =>
      _.uniqBy(
        [
          ...bases.map((base) => ({ label: optionLabel(base.name, base.id), value: base.id })),
          ...(selectedBaseId ? [{ label: optionLabel(config?.baseId === selectedBaseId ? 'Saved base' : 'Selected base', selectedBaseId), value: selectedBaseId }] : []),
        ],
        'value'
      ),
    [bases, config?.baseId, selectedBaseId]
  )
  const setupStepLabels = useMemo(
    () => [
      t('page.admin.airtable.step.connect', { defaultValue: 'Connect Airtable' }),
      t('page.admin.airtable.step.setupPatients', { defaultValue: 'Set up Patients table' }),
      t('page.admin.airtable.step.mirrorPatients', { defaultValue: 'Mirror patients' }),
      t('page.admin.airtable.step.inboundSync', { defaultValue: 'Enable inbound sync' }),
      t('page.admin.airtable.step.workflowTables', { defaultValue: 'Use in Airtable' }),
    ],
    [t]
  )
  const diagnosticSummary = useMemo(
    () =>
      [
        'Airtable integration diagnostic',
        `Establishment ID: ${establishmentId || 'unknown'}`,
        `Base ID: ${config?.baseId || 'not configured'}`,
        `Patients table ID: ${patientsTableStatus?.tableId || config?.patientTableId || 'not configured'}`,
        `API key configured: ${config?.hasApiKey ? 'yes' : 'no'}`,
        `Patients table ready: ${patientsTableReady ? 'yes' : 'no'}`,
        `Inbound sync enabled: ${inboundSyncStatus?.enabled ? 'yes' : 'no'}`,
        `Webhook mode: ${inboundSyncStatus?.webhookMode || 'not configured'}`,
        `Webhook configured: ${inboundSyncStatus?.webhookConfigured ? 'yes' : 'no'}`,
        `Webhook ID: ${inboundSyncStatus?.webhookId || 'not configured'}`,
        `Last webhook notification: ${inboundSyncStatus?.lastWebhookNotificationAt || 'none'}`,
        `Last reconciliation: ${inboundSyncStatus?.lastReconciledAt || 'none'}`,
        `Needs review count: ${inboundSyncStatus?.needsReviewCount ?? 'unknown'}`,
        `Recent failure count: ${inboundSyncStatus?.recentFailureCount ?? 'unknown'}`,
      ].join('\n'),
    [config?.baseId, config?.hasApiKey, config?.patientTableId, establishmentId, inboundSyncStatus, patientsTableReady, patientsTableStatus?.tableId]
  )

  const loadBases = async () => {
    if (!establishmentId) return
    const result = await discoverBases({ establishmentId, apiKey: apiKey || undefined }).unwrap()
    setBases(result.bases)
  }

  useEffect(() => {
    if (!establishmentId || !config?.hasApiKey || bases.length) return
    discoverBases({ establishmentId })
      .unwrap()
      .then((result) => {
        setBases(result.bases)
      })
      .catch(() => undefined)
  }, [bases.length, config?.hasApiKey, discoverBases, establishmentId])

  if (!isLoadingLicenses && !hasAirtableLicense) return <AccessDenied />

  const connectionDone = hasSavedConnection
  const patientsTableDone = patientsTableReady

  return (
    <PageContainer>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          if (!establishmentId) return
          await updateConfig({
            establishmentId,
            baseId: values.baseId || config?.baseId,
            apiKey: values.apiKey || undefined,
          }).unwrap()
          form.setValue('apiKey', '')
          setExportResult(undefined)
        })}
      >
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 2,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'primary.contrastText',
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      width: 48,
                      height: 48,
                      border: '1px solid rgba(255,255,255,0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <CloudSyncIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {t('page.admin.airtable.title', { defaultValue: 'Airtable integration' })}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25, opacity: 0.85 }}>
                      {t('page.admin.airtable.subtitle', {
                        defaultValue: 'Mirror MedConnect patients into an Airtable Patients table.',
                      })}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.35)' },
                    '& .MuiStepIcon-root.Mui-active': { color: 'common.white' },
                    '& .MuiStepIcon-root.Mui-completed': { color: 'success.light' },
                    '& .MuiStepIcon-text': { fill: (theme) => theme.palette.primary.main },
                    '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.85) !important', fontSize: 12 },
                    '& .MuiStepLabel-label.Mui-active': { color: 'common.white !important', fontWeight: 600 },
                    '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    aria-label={String(t('page.admin.airtable.stepper.label', { defaultValue: 'Airtable setup progress' }))}
                  >
                    {setupStepLabels.map((label, idx) => (
                      <Step key={label} completed={idx < activeStep}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <SectionCard
                stepNumber={1}
                icon={<LinkIcon fontSize="small" />}
                title={t('page.admin.airtable.connect.title', { defaultValue: 'Connect Airtable' })}
                description={t('page.admin.airtable.connect.description', {
                  defaultValue: 'Provide an Airtable API key and pick the base to mirror patients into.',
                })}
                done={connectionDone}
              >
                {config && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={config.hasApiKey ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                      label={
                        config.hasApiKey
                          ? t('page.admin.airtable.chip.apiKeyConfigured', { defaultValue: 'API key configured' })
                          : t('page.admin.airtable.chip.apiKeyMissing', { defaultValue: 'API key missing' })
                      }
                      color={config.hasApiKey ? 'success' : 'default'}
                      variant={config.hasApiKey ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip
                      icon={config.baseId ? <StorageIcon /> : <RadioButtonUncheckedIcon />}
                      label={
                        config.baseId
                          ? t('page.admin.airtable.chip.baseSelected', { defaultValue: 'Base: {{baseId}}', baseId: config.baseId })
                          : t('page.admin.airtable.chip.baseMissing', { defaultValue: 'No base selected' })
                      }
                      color={config.baseId ? 'success' : 'default'}
                      variant={config.baseId ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </Stack>
                )}
                <TextInputController
                  control={form.control}
                  fullWidth
                  disabled={isLoading}
                  helperText={
                    config?.hasApiKey
                      ? t('page.admin.airtable.apiKey.helperConfigured', { defaultValue: 'Leave empty to use the saved API key.' })
                      : t('page.admin.airtable.apiKey.helperEmpty', {
                          defaultValue: 'Generate a personal access token at airtable.com/create/tokens.',
                        })
                  }
                  label={t('field.airtable.apiKey.label', { defaultValue: 'Airtable API key' })}
                  name="apiKey"
                  type="password"
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'stretch' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SelectInputController
                      control={form.control}
                      disabled={isLoading || !baseOptions.length}
                      fullWidth
                      label={t('field.airtable.base.label', { defaultValue: 'Airtable base' })}
                      name="baseId"
                      options={baseOptions}
                    />
                  </Box>
                  <LoadingButton
                    disabled={!establishmentId || (!apiKey && !config?.hasApiKey)}
                    loading={isDiscoveringBases}
                    onClick={loadBases}
                    startIcon={<CloudSyncIcon />}
                    variant="outlined"
                    sx={{ height: 56, minWidth: { sm: 160 } }}
                  >
                    {t('button.airtable.loadBases.label', { defaultValue: 'Load bases' })}
                  </LoadingButton>
                </Stack>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    loading={isUpdating}
                    startIcon={<SaveIcon />}
                    type="submit"
                    variant="contained"
                    sx={ACTION_BUTTON_SX}
                  >
                    {t('button.airtable.saveConnection.label', { defaultValue: 'Save connection' })}
                  </LoadingButton>
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SectionCard
                stepNumber={2}
                icon={<TableRowsIcon fontSize="small" />}
                title={t('page.admin.airtable.patientsTable.title', { defaultValue: 'Set up Patients table' })}
                description={t('page.admin.airtable.patientsTable.description', {
                  defaultValue: 'MedConnect creates and maintains the Patients table schema in your base.',
                })}
                done={patientsTableDone}
                disabled={!hasSavedConnection}
              >
                {!hasSavedConnection && (
                  <Alert severity="info" variant="outlined">
                    {t('page.admin.airtable.patientsTable.saveFirst', {
                      defaultValue: 'Save the connection before setting up the Patients table.',
                    })}
                  </Alert>
                )}
                {hasSavedConnection && patientsTableStatus && (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={patientsTableStatus.exists ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                        label={
                          patientsTableStatus.exists
                            ? t('page.admin.airtable.chip.tableFound', { defaultValue: 'Table: {{tableId}}', tableId: patientsTableStatus.tableId })
                            : t('page.admin.airtable.chip.tableMissing', { defaultValue: 'No managed table yet' })
                        }
                        color={patientsTableStatus.exists ? 'success' : 'default'}
                        variant={patientsTableStatus.exists ? 'filled' : 'outlined'}
                        size="small"
                        sx={{ maxWidth: '100%' }}
                      />
                      {patientsTableStatus.exists && (
                        <Chip
                          icon={patientsTableStatus.missingFieldNames.length === 0 ? <CheckCircleIcon /> : undefined}
                          label={
                            patientsTableStatus.missingFieldNames.length === 0
                              ? t('page.admin.airtable.chip.allFieldsPresent', { defaultValue: 'Schema OK' })
                              : t('page.admin.airtable.chip.fieldsMissing', {
                                  defaultValue: '{{count}} field(s) missing',
                                  count: patientsTableStatus.missingFieldNames.length,
                                })
                          }
                          color={patientsTableStatus.missingFieldNames.length === 0 ? 'success' : 'warning'}
                          variant={patientsTableStatus.missingFieldNames.length === 0 ? 'filled' : 'outlined'}
                          size="small"
                        />
                      )}
                    </Stack>
                    {patientsTableStatus.exists && patientsTableStatus.missingFieldNames.length > 0 && (
                      <Alert severity="warning" variant="outlined">
                        {t('page.admin.airtable.patientsTable.missingFields', {
                          defaultValue: 'Will add on repair: {{missingFields}}',
                          missingFields: patientsTableStatus.missingFieldNames.join(', '),
                        })}
                      </Alert>
                    )}
                  </Stack>
                )}
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    disabled={!canSetupPatientsTable || isLoadingPatientsTableStatus}
                    loading={isInitializingPatientsTable}
                    startIcon={<TableRowsIcon />}
                    variant="contained"
                    sx={ACTION_BUTTON_SX}
                    onClick={async () => {
                      if (!establishmentId) return
                      await initializePatientsTable(establishmentId).unwrap()
                      setExportResult(undefined)
                    }}
                  >
                    {patientsTableStatus?.exists
                      ? t('button.airtable.syncPatientsTable.label', { defaultValue: 'Repair table' })
                      : t('button.airtable.createPatientsTable.label', { defaultValue: 'Create table' })}
                  </LoadingButton>
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard
                stepNumber={3}
                icon={<CloudSyncIcon fontSize="small" />}
                title={t('page.admin.airtable.mirror.title', { defaultValue: 'Mirror MedConnect patients' })}
                description={t('page.admin.airtable.mirror.description', {
                  defaultValue: 'Push existing MedConnect patients to Airtable. Re-running keeps records in sync.',
                })}
                disabled={!patientsTableReady}
              >
                {!patientsTableReady && (
                  <Alert severity="info" variant="outlined">
                    {t('page.admin.airtable.mirror.setupFirst', {
                      defaultValue: 'Set up the managed Patients table before mirroring MedConnect patients.',
                    })}
                  </Alert>
                )}
                {exportResult && (
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2.4}>
                      <StatCard
                        label={t('page.admin.airtable.stat.scanned', { defaultValue: 'Scanned' })}
                        value={exportResult.scanned}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <StatCard
                        label={t('page.admin.airtable.stat.created', { defaultValue: 'Created' })}
                        value={exportResult.created}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <StatCard
                        label={t('page.admin.airtable.stat.updated', { defaultValue: 'Updated' })}
                        value={exportResult.updated}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <StatCard
                        label={t('page.admin.airtable.stat.unchanged', { defaultValue: 'Unchanged' })}
                        value={exportResult.unchanged}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <StatCard
                        label={t('page.admin.airtable.stat.skipped', { defaultValue: 'Skipped' })}
                        value={exportResult.skippedInvalid}
                        color={exportResult.skippedInvalid > 0 ? 'warning' : 'primary'}
                      />
                    </Grid>
                  </Grid>
                )}
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    disabled={!canMirrorPatients}
                    loading={isExportingMedConnectPatients}
                    startIcon={<CloudSyncIcon />}
                    variant="contained"
                    sx={ACTION_BUTTON_SX}
                    onClick={async () => {
                      if (!establishmentId) return
                      const result = await exportMedConnectPatientsToAirtable(establishmentId).unwrap()
                      setExportResult(result)
                    }}
                  >
                    {t('button.airtable.exportMedConnectPatients.label', { defaultValue: 'Mirror patients' })}
                  </LoadingButton>
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={7}>
              <SectionCard
                stepNumber={4}
                icon={<CloudSyncIcon fontSize="small" />}
                title={t('page.admin.airtable.inbound.title', { defaultValue: 'Enable real-time Airtable sync' })}
                description={t('page.admin.airtable.inbound.description', {
                  defaultValue: 'MedConnect manages Airtable Webhooks API setup for Patients changes.',
                })}
                done={inboundSyncDone}
                disabled={!patientsTableReady}
              >
                {!patientsTableReady && (
                  <Alert severity="info" variant="outlined">
                    {t('page.admin.airtable.inbound.setupFirst', {
                      defaultValue: 'Set up the managed Patients table before enabling inbound sync.',
                    })}
                  </Alert>
                )}
                {patientsTableReady && inboundSyncStatus && (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={t('page.admin.airtable.chip.webhookMode', {
                          defaultValue: 'Mode: {{mode}}',
                          mode:
                            inboundSyncStatus.webhookMode === 'airtable_api'
                              ? t('page.admin.airtable.webhookMode.airtableApi', { defaultValue: 'Airtable Webhooks API' })
                              : t('page.admin.airtable.webhookMode.manual', { defaultValue: 'Manual' }),
                        })}
                        color={inboundSyncStatus.webhookMode === 'airtable_api' ? 'success' : 'default'}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        icon={inboundSyncStatus.enabled ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                        label={
                          inboundSyncStatus.enabled
                            ? t('page.admin.airtable.chip.inboundEnabled', { defaultValue: 'Inbound sync enabled' })
                            : t('page.admin.airtable.chip.inboundDisabled', { defaultValue: 'Inbound sync disabled' })
                        }
                        color={inboundSyncStatus.enabled ? 'success' : 'default'}
                        variant={inboundSyncStatus.enabled ? 'filled' : 'outlined'}
                        size="small"
                      />
                      <Chip
                        label={t('page.admin.airtable.chip.needsReview', {
                          defaultValue: '{{count}} needs review',
                          count: inboundSyncStatus.needsReviewCount,
                        })}
                        color={inboundSyncStatus.needsReviewCount ? 'warning' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={t('page.admin.airtable.chip.recentFailures', {
                          defaultValue: '{{count}} recent failures',
                          count: inboundSyncStatus.recentFailureCount,
                        })}
                        color={inboundSyncStatus.recentFailureCount ? 'error' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                      {inboundSyncStatus.lastReconciledAt && (
                        <Chip
                          label={t('page.admin.airtable.chip.lastReconciledAt', {
                            defaultValue: 'Last reconciliation: {{date}}',
                            date: new Date(inboundSyncStatus.lastReconciledAt).toLocaleString(),
                          })}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {inboundSyncStatus.lastWebhookNotificationAt && (
                        <Chip
                          label={t('page.admin.airtable.chip.lastWebhookNotificationAt', {
                            defaultValue: 'Last webhook: {{date}}',
                            date: new Date(inboundSyncStatus.lastWebhookNotificationAt).toLocaleString(),
                          })}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Stack>
                    {inboundSyncStatus.missingPublicWebhookUrl && (
                      <Alert severity="warning" variant="outlined">
                        {t('page.admin.airtable.webhookPublicUrl.missing', {
                          defaultValue:
                            'Real-time sync needs AIRTABLE_WEBHOOK_PUBLIC_BASE_URL on the backend. For local testing, set it to your ngrok HTTPS URL.',
                        })}
                      </Alert>
                    )}
                    {webhookUrl && (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {t('page.admin.airtable.webhookUrl.label', { defaultValue: 'Registered notification URL' })}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                            {webhookUrl}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            onClick={async () => {
                              await navigator.clipboard.writeText(webhookUrl)
                              setCopiedWebhook(true)
                              window.setTimeout(() => setCopiedWebhook(false), 1800)
                            }}
                          >
                            {copiedWebhook
                              ? t('button.airtable.copied.label', { defaultValue: 'Copied' })
                              : t('button.airtable.copy.label', { defaultValue: 'Copy' })}
                          </Button>
                        </Stack>
                        {inboundSyncStatus.webhookId && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t('page.admin.airtable.webhookId.label', {
                              defaultValue: 'Webhook ID: {{webhookId}}',
                              webhookId: inboundSyncStatus.webhookId,
                            })}
                          </Typography>
                        )}
                      </Paper>
                    )}
                    <Alert severity="info" variant="outlined">
                      {t('page.admin.airtable.webhookApi.instructions', {
                        defaultValue:
                          'MedConnect registers and verifies the Airtable webhook automatically. Background reconciliation still recovers missed events.',
                      })}
                    </Alert>
                    {verificationResult && (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {t('page.admin.airtable.verify.title', { defaultValue: 'Setup verification' })}
                          </Typography>
                          {verificationResult.steps.map((step) => (
                            <Stack key={step.key} direction="row" spacing={1} alignItems="center">
                              {step.status === 'success' ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : step.status === 'failed' ? (
                                <RadioButtonUncheckedIcon color="error" fontSize="small" />
                              ) : (
                                <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                              )}
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {t(`page.admin.airtable.verify.step.${step.key}`, { defaultValue: step.label })}
                              </Typography>
                              {step.sanitizedError && (
                                <Typography variant="caption" color="error" sx={{ textAlign: 'right' }}>
                                  {step.sanitizedError}
                                </Typography>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}
                <Box sx={{ flex: 1 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={1.5}>
                  <LoadingButton
                    disabled={!establishmentId || !inboundSyncDone}
                    loading={isVerifyingInboundSync}
                    startIcon={<CheckCircleIcon />}
                    variant="outlined"
                    sx={ACTION_BUTTON_SX}
                    onClick={async () => {
                      if (!establishmentId) return
                      const result = await verifyAirtableInboundSync(establishmentId).unwrap()
                      setVerificationResult(result)
                    }}
                  >
                    {t('button.airtable.verifyInboundSetup.label', { defaultValue: 'Verify setup' })}
                  </LoadingButton>
                  <LoadingButton
                    disabled={!establishmentId || !patientsTableReady}
                    loading={isEnablingInboundSync}
                    startIcon={<CloudSyncIcon />}
                    variant="contained"
                    sx={ACTION_BUTTON_SX}
                    onClick={async () => {
                      if (!establishmentId) return
                      await enableAirtableInboundSync(establishmentId).unwrap()
                      setVerificationResult(undefined)
                    }}
                  >
                    {inboundSyncDone
                      ? t('button.airtable.refreshInboundSetup.label', { defaultValue: 'Refresh setup' })
                      : t('button.airtable.enableInboundSync.label', { defaultValue: 'Enable inbound sync' })}
                  </LoadingButton>
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={5}>
              <SectionCard
                stepNumber={5}
                icon={<TableRowsIcon fontSize="small" />}
                title={t('page.admin.airtable.workflow.title', { defaultValue: 'Use Patients in Visits and Bloodwork' })}
                description={t('page.admin.airtable.workflow.description', {
                  defaultValue: 'Keep customer workflow tables in Airtable and link them to the managed Patients table.',
                })}
                done={patientsTableReady}
                disabled={!patientsTableReady}
              >
                <Alert severity="info" variant="outlined">
                  {t('page.admin.airtable.workflow.guidance', {
                    defaultValue:
                      'In Visits or Bloodwork, add a linked-record field to Patients. Then add lookup fields for DOB, phone, email, insurance, or MedConnect Patient ID. MedConnect does not sync Visits or Bloodwork rows in v1.',
                  })}
                </Alert>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t('page.admin.airtable.troubleshooting.title', { defaultValue: 'Troubleshooting' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('page.admin.airtable.troubleshooting.subtitle', {
                          defaultValue: 'Use these checks when Airtable or MedConnect sync status looks wrong.',
                        })}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={async () => {
                      await navigator.clipboard.writeText(diagnosticSummary)
                      setCopiedDiagnostic(true)
                      window.setTimeout(() => setCopiedDiagnostic(false), 1800)
                    }}
                  >
                    {copiedDiagnostic
                      ? t('button.airtable.copied.label', { defaultValue: 'Copied' })
                      : t('button.airtable.copyDiagnostic.label', { defaultValue: 'Copy diagnostic summary' })}
                  </Button>
                </Stack>

                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('page.admin.airtable.troubleshooting.statuses.title', { defaultValue: 'Status meanings' })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.status.synced.primary', { defaultValue: 'Synced' })}
                          secondary={t('page.admin.airtable.troubleshooting.status.synced.secondary', {
                            defaultValue: 'The row was accepted and Airtable was refreshed with MedConnect canonical values.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.status.needsReview.primary', { defaultValue: 'Needs Review' })}
                          secondary={t('page.admin.airtable.troubleshooting.status.needsReview.secondary', {
                            defaultValue: 'One patient row was stopped because accepting the edit could overwrite data unsafely.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.status.failed.primary', { defaultValue: 'Failed' })}
                          secondary={t('page.admin.airtable.troubleshooting.status.failed.secondary', {
                            defaultValue: 'The sync could not complete because of missing required data, permissions, configuration, or a temporary service error.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.status.retry.primary', { defaultValue: 'Retry Sync' })}
                          secondary={t('page.admin.airtable.troubleshooting.status.retry.secondary', {
                            defaultValue: 'After correcting the row, toggle Retry Sync to ask MedConnect to process it again.',
                          })}
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('page.admin.airtable.troubleshooting.needsReview.title', { defaultValue: 'How to resolve Needs Review' })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.needsReview.compare', {
                            defaultValue: 'Compare the Airtable patient row with the MedConnect patient profile.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.needsReview.fix', {
                            defaultValue: 'Fix the incorrect side. Do not manually edit Sync Status, Last Synced At, Last Sync Error, or MedConnect Patient ID.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.needsReview.retry', {
                            defaultValue: 'Toggle Retry Sync or edit the patient field again. MedConnect will clear Needs Review after a successful sync.',
                          })}
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('page.admin.airtable.troubleshooting.common.title', { defaultValue: 'Common checks' })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.common.webhook.primary', {
                            defaultValue: 'Airtable edit does not update MedConnect',
                          })}
                          secondary={t('page.admin.airtable.troubleshooting.common.webhook.secondary', {
                            defaultValue: 'Check Last webhook, run Verify setup, and confirm the webhook URL points to the current backend or ngrok URL.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.common.mirror.primary', {
                            defaultValue: 'MedConnect edit does not update Airtable',
                          })}
                          secondary={t('page.admin.airtable.troubleshooting.common.mirror.secondary', {
                            defaultValue: 'Confirm Airtable is enabled for the establishment, the Patients table exists, and the API key still has write access.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.common.permissions.primary', {
                            defaultValue: 'Permission or base errors',
                          })}
                          secondary={t('page.admin.airtable.troubleshooting.common.permissions.secondary', {
                            defaultValue: 'Reload bases with the saved API key, select the correct base, save, then refresh setup.',
                          })}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={t('page.admin.airtable.troubleshooting.common.portal.primary', {
                            defaultValue: 'Portal account email was sent',
                          })}
                          secondary={t('page.admin.airtable.troubleshooting.common.portal.secondary', {
                            defaultValue: 'Patient email triggers portal account creation or linking automatically in this version.',
                          })}
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </form>
    </PageContainer>
  )
}
